import { HardwareService } from '../../../HardwareService';
import type { BarcodeData } from '../../types';
import type { DecoderProbe, Pdf417Decoder } from './types';
import { latin1Bytes } from './types';

type DetectedBarcode = { rawValue: string };
type Detector = { detect(source: ImageBitmapSource): Promise<DetectedBarcode[]> };
type DetectorConstructor = {
  new (options: { formats: string[] }): Detector;
  getSupportedFormats(): Promise<string[]>;
};

/**
 * The platform Shape Detection API: free where it exists, mainly Android Chromium. PDF417 support is
 * probed rather than assumed, because the constructor can exist while getSupportedFormats() omits it.
 */
export const barcodeDetectorProbe: DecoderProbe = {
  name: 'BarcodeDetector',

  async create(): Promise<Pdf417Decoder | null> {
    const Ctor = (globalThis as { BarcodeDetector?: DetectorConstructor }).BarcodeDetector;
    if (!Ctor) {
      return null;
    }

    try {
      const formats = await Ctor.getSupportedFormats();
      if (!formats.includes('pdf417')) {
        return null;
      }
      const detector = new Ctor({ formats: ['pdf417'] });
      return {
        name: barcodeDetectorProbe.name,
        async decode(frame: ImageData): Promise<BarcodeData | null> {
          try {
            const [found] = await detector.detect(frame);
            return found?.rawValue ? { bytes: latin1Bytes(found.rawValue), text: found.rawValue } : null;
          } catch {
            return null;
          }
        },
      };
    } catch (error) {
      // Absorbed: a rejection escaping create() takes the whole ladder down and the wasm rung below
      // it — the only one with real-image coverage — is never reached.
      HardwareService.logger.d('BarcodeDetector unavailable', { error });
      return null;
    }
  },
};
